import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import Layout from '../../components/layouts/Main';

import TransporterForm from '../../components/master/TransporterForm';
import { Link } from 'react-router-dom';

const TransporterMaster = () => {
    const [transporters, setTransporters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedTransporter, setSelectedTransporter] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [transportersPerPage] = useState(10);

    const fetchTransporters = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/transporters`, {
                params: {
                    search: searchTerm,
                    page: currentPage,
                    limit: transportersPerPage
                }
            });
            setTransporters(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch transporters');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, currentPage, transportersPerPage]);

    useEffect(() => {
        fetchTransporters();
    }, [fetchTransporters]);

    const handleSave = async (transporterData) => {
        setLoading(true);
        try {
            if (transporterData._id) {
                await axios.put(`${config.API_BASE_URL}/transporters/${transporterData._id}`, transporterData);
                toast.success('Transporter updated successfully');
            } else {
                await axios.post(`${config.API_BASE_URL}/transporters`, transporterData);
                toast.success('Transporter created successfully');
            }
            fetchTransporters();
            setShowModal(false);
            setSelectedTransporter(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save transporter');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedTransporter) return;
        setLoading(true);
        try {
            await axios.delete(`${config.API_BASE_URL}/transporters/${selectedTransporter._id}`);
            toast.success('Transporter deactivated successfully');
            fetchTransporters();
            setShowDeleteConfirm(false);
            setSelectedTransporter(null);
        } catch (err) {
            toast.error('Failed to deactivate transporter');
        } finally {
            setLoading(false);
        }
    };

    const indexOfLastTransporter = currentPage * transportersPerPage;
    const indexOfFirstTransporter = indexOfLastTransporter - transportersPerPage;
    const currentTransporters = transporters.slice(indexOfFirstTransporter, indexOfLastTransporter);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                    <li className="breadcrumb-item active">Transporters</li>
                </ol>
            </nav>

            <Row className="mb-3">
                <Col>
                    <h2>Transporter Management</h2>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => { setSelectedTransporter(null); setShowModal(true); }}>
                        Add Transporter
                    </Button>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Form.Control
                        type="text"
                        placeholder="Search by name or contact"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Col>
            </Row>

            {loading && !transporters.length ? (
                <Spinner animation="border" />
            ) : transporters.length === 0 ? (
                <Alert variant="info">No transporters found.</Alert>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Contact Number</th>
                                <th>GSTIN</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentTransporters.map(t => (
                                <tr key={t._id}>
                                    <td>{t.name}</td>
                                    <td>{t.contactNumber}</td>
                                    <td>{t.gstin}</td>
                                    <td>
                                        <span className={`badge bg-${t.isActive ? 'success' : 'danger'}`}>
                                            {t.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <Link to={`/transporters/${t._id}`} className="btn btn-primary btn-sm me-2">
                                            View
                                        </Link>
                                        <Button variant="info" size="sm" onClick={() => { setSelectedTransporter(t); setShowModal(true); }} className="me-2">
                                            Edit
                                        </Button>
                                        {t.isActive && (
                                            <Button variant="danger" size="sm" onClick={() => { setSelectedTransporter(t); setShowDeleteConfirm(true); }}>
                                                Deactivate
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination
                        itemsPerPage={transportersPerPage}
                        totalItems={transporters.length}
                        paginate={paginate}
                    />
                </>
            )}

            <Modal show={showModal} onHide={() => { setShowModal(false); setSelectedTransporter(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedTransporter ? 'Edit' : 'Add'} Transporter</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TransporterForm
                        transporter={selectedTransporter}
                        onSave={handleSave}
                        onCancel={() => { setShowModal(false); setSelectedTransporter(null); }}
                        loading={loading}
                    />
                </Modal.Body>
            </Modal>

            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate the transporter "{selectedTransporter?.name}"?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </Layout>
    );
};

const Pagination = ({ itemsPerPage, totalItems, paginate }) => {
    const pageNumbers = [];

    for (let i = 1; i <= Math.ceil(totalItems / itemsPerPage); i++) {
        pageNumbers.push(i);
    }

    return (
        <nav>
            <ul className="pagination">
                {pageNumbers.map(number => (
                    <li key={number} className="page-item">
                        <a onClick={() => paginate(number)} href="#!" className="page-link">
                            {number}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default TransporterMaster;
